"use client";

import { useEffect, useId, useRef, useSyncExternalStore, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

const subscribe = () => () => undefined;

/** Native modal top layer avoids clipping/positioning inside tables and workspaces. */
export default function CommerceDialog({ open, title, description, busy = false, alert = false, variant = "center", showHeader = true, returnFocusRef, fallbackFocusRef, onClose, children }: {
  open: boolean; title: string; description?: string; busy?: boolean; alert?: boolean; variant?: "center" | "drawer"; showHeader?: boolean;
  returnFocusRef?: RefObject<HTMLElement | null>; fallbackFocusRef?: RefObject<HTMLElement | null>; onClose: () => void; children: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const mounted = useSyncExternalStore(subscribe, () => true, () => false);
  useEffect(() => {
    const element = dialog.current;
    if (!element || !open) return;
    const previousFocus = returnFocusRef?.current ?? document.activeElement;
    const fallbackFocus = fallbackFocusRef?.current;
    const previousOverflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = "hidden";
    element.querySelector<HTMLElement>("[data-dialog-focus], input:not([disabled]), textarea:not([disabled]), button:not([disabled])")?.focus();
    return () => {
      if (element.open) element.close();
      document.body.style.overflow = previousOverflow;
      let restored = false;
      if (previousFocus instanceof HTMLElement) {
        if (previousFocus.isConnected) { previousFocus.focus(); restored = true; }
        // Desktop rows and mobile cards share an action key, not a DOM node.
        const actionKey = previousFocus.dataset.dialogReturnFocus;
        if (document.activeElement !== previousFocus && actionKey) {
          const keyedControl = Array.from(document.querySelectorAll<HTMLElement>("[data-dialog-return-focus]"))
            .find((control) => control.dataset.dialogReturnFocus === actionKey
              && control.getClientRects().length > 0 && !control.matches(":disabled"));
          if (keyedControl) { keyedControl.focus(); restored = true; }
        }
      }
      if (!restored && fallbackFocus?.isConnected) fallbackFocus.focus();
    };
  }, [open, mounted, returnFocusRef, fallbackFocusRef]);

  // A hidden responsive table/card ancestor must never hide an open modal.
  return mounted ? createPortal(<dialog ref={dialog} className="vintera-commerce-dialog" data-variant={variant} role={alert ? "alertdialog" : "dialog"}
    aria-modal="true" aria-labelledby={showHeader ? titleId : undefined} aria-label={showHeader ? undefined : title} aria-describedby={description ? descriptionId : undefined}
    onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
    onKeyDown={(event) => {
      if (event.key !== "Tab") return;
      const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      )).filter((control) => control.getClientRects().length > 0);
      const first = controls[0]; const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }}
    onClick={(event) => {
      if (event.target !== dialog.current || busy || alert) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
    {showHeader && <header className="vintera-dialog-header"><div><h2 id={titleId}>{title}</h2>
      {description && <p id={descriptionId}>{description}</p>}</div>
      <button type="button" disabled={busy} onClick={onClose} aria-label={`Close ${title}`} className="vintera-dialog-close">×</button>
    </header>}
    {!showHeader && description && <p id={descriptionId} className="sr-only">{description}</p>}
    {children}
  </dialog>, document.body) : null;
}
